import { useMutation } from '@tanstack/react-query'
import { useContext } from 'react'

import { useWidgetConfigContext } from '@/contexts/widget-config-context'
import { dispatchAction, type DispatchActionRequest, type DispatchActionResponse } from '@/features/conversation/apis/dispatch-action'
import { CardActionContext } from '@/features/conversation/contexts/card-action-context'
import { createDebugLogger, getFilePathFromUrl } from '@/utils/logger'

const logger = createDebugLogger(getFilePathFromUrl(import.meta.url))

export const useDispatchAction = () => {
  const { widgetConfig } = useWidgetConfigContext()
  // cards drawn with no conversation behind them (the render SDK) hand their buttons to the page that drew them
  const handler = useContext(CardActionContext)

  return useMutation({
    mutationFn: async (data: Omit<DispatchActionRequest, 'widgetConfig'>): Promise<DispatchActionResponse> => {
      logger.debug('useDispatchAction', 'Mutation triggered', data)
      if (handler) {
        const started = Date.now()
        const r = await handler({ id: data.actionUlid, data: data.parameters ?? {}, sourceMessageUlid: data.sourceMessageUlid })
        const ok = r ? r.success : true
        return {
          success: ok,
          response: { body: r?.message ? { message: r.message } : {}, status: 200 },
          error_code: ok ? null : 'action_failed',
          error_message: ok ? null : r?.message || 'Action failed',
          duration_ms: Date.now() - started,
          audit_ulid: '',
        }
      }
      return dispatchAction({
        ...data,
        widgetConfig,
      })
    },
    onSuccess: (data, variables) => {
      logger.debug('useDispatchAction', 'Mutation success', { data, variables })
    },
    onError: (error, variables) => {
      logger.debug('useDispatchAction', 'Mutation error', { error, variables })
    },
  })
}
